import { useEffect, useState } from "react";
import { lockersApi, type DeliveryInput } from "@/lib/api";

// Courier Guy (full address + phone) or Pudo locker (locker + cellphone).
export default function DeliveryPicker({
  value,
  onChange,
}: {
  value: DeliveryInput;
  onChange: (v: DeliveryInput) => void;
}) {
  const [q, setQ] = useState("");
  const [lockers, setLockers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (value.method !== "pudo") return;
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      lockersApi
        .list(q)
        .then((r) => live && setLockers(r.lockers))
        .catch(() => live && setLockers([]))
        .finally(() => live && setLoading(false));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [q, value.method]);

  const set = (patch: Partial<DeliveryInput>) => onChange({ ...value, ...patch });

  const fieldCls =
    "w-full rounded-lg bg-[#0b111c] border border-sky-500/20 px-4 py-3 text-white placeholder-gray-500 focus:border-sky-400 outline-none";

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {(["courier", "pudo"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => set({ method: m })}
            className={`rounded-xl border p-4 text-left transition ${
              value.method === m
                ? "border-sky-400 bg-sky-500/15"
                : "border-slate-700 bg-[#0b111c] hover:bg-slate-800"
            }`}
          >
            <p className="font-bold text-white">{m === "courier" ? "🚚 Courier Guy" : "📦 Pudo Locker"}</p>
            <p className="text-xs text-gray-400 mt-1">
              {m === "courier" ? "Door-to-door to your address" : "Collect from a locker near you"}
            </p>
          </button>
        ))}
      </div>

      {value.method === "courier" && (
        <div className="space-y-3">
          <textarea
            placeholder="Full delivery address (street, suburb, city, postal code)"
            value={value.address ?? ""}
            onChange={(e) => set({ address: e.target.value })}
            rows={3}
            className={fieldCls}
          />
          <input
            placeholder="Contact telephone number"
            value={value.phone ?? ""}
            onChange={(e) => set({ phone: e.target.value })}
            className={fieldCls}
          />
        </div>
      )}

      {value.method === "pudo" && (
        <div className="space-y-3">
          <input
            placeholder="Cellphone number (for collection SMS)"
            value={value.phone ?? ""}
            onChange={(e) => set({ phone: e.target.value })}
            className={fieldCls}
          />
          {value.locker_code ? (
            <div className="flex items-center justify-between rounded-lg bg-sky-500/10 border border-sky-400/30 px-4 py-3">
              <div>
                <p className="text-white font-semibold">{value.locker_name}</p>
                <p className="text-xs text-gray-400">{value.locker_code}</p>
              </div>
              <button
                type="button"
                onClick={() => set({ locker_code: "", locker_name: "" })}
                className="text-sky-300 text-sm hover:underline"
              >
                Change
              </button>
            </div>
          ) : (
            <>
              <input
                placeholder="Search a locker by town or name…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className={fieldCls}
              />
              <div className="max-h-64 overflow-auto rounded-lg border border-sky-500/10 divide-y divide-sky-500/10">
                {loading && <p className="p-3 text-gray-500 text-sm">Searching…</p>}
                {!loading && lockers.length === 0 && (
                  <p className="p-3 text-gray-500 text-sm">No lockers found.</p>
                )}
                {lockers.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => set({ locker_code: l.code, locker_name: l.name })}
                    className="block w-full text-left px-4 py-3 hover:bg-sky-500/10"
                  >
                    <p className="text-white text-sm font-medium">{l.name}</p>
                    <p className="text-xs text-gray-500">
                      {[l.town, l.province].filter(Boolean).join(", ")} · {l.code}
                    </p>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
